import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from 'src/app/components/brokeragency/member-addedit/member-addedit.component';
import {
  CampaignInterface,
  MarketingArchiveStatusFilter,
} from 'src/app/interfaces/campaigns/campaign.interface';
import {
  MarketingSequence,
  MarketingSequenceStep,
  MarketingSequenceStatus,
  PostMarketingSequence,
} from 'src/app/interfaces/campaigns/sequence.interface';
import {
  InboundFileFeed,
  InboundFileFeedsService,
} from 'src/app/services/inbound-file-feeds/inbound-file-feeds.service';
import { MarketingCampaignsService } from 'src/app/services/marketing-campaigns/marketing-campaigns.service';
import { MarketingSequencesService } from 'src/app/services/marketing-campaigns/marketing-sequences.service';
import { UiAlertService } from 'src/app/services/ui-alert/ui-alert.service';

const MAX_SEQUENCE_STEPS = 8;

@Component({
  selector: 'app-marketing-campaigns-sequences',
  templateUrl: './marketing-campaigns-sequences.component.html',
  styleUrls: ['./marketing-campaigns-sequences.component.scss'],
})
export class MarketingCampaignsSequencesComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  columnNames = ['name', 'status', 'inboundFeed', 'timezone', 'steps', 'createdAt', 'actionmenu'];
  tableDataSource = new MatTableDataSource<any>();
  campaigns: CampaignInterface[] = [];
  inboundFeeds: InboundFileFeed[] = [];
  sequenceCount = 0;
  sequenceArchiveStatusFilter: MarketingArchiveStatusFilter = 'active';
  archiveStatusOptions: Array<{ label: string; value: MarketingArchiveStatusFilter }> = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Archived', value: 'archived' },
  ];

  private readonly defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  private readonly timezoneOptions = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'UTC',
  ];

  constructor(
    private sequencesService: MarketingSequencesService,
    private campaignsService: MarketingCampaignsService,
    private inboundFeedsService: InboundFileFeedsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
  }

  load(): void {
    this.sequencesService.getAll(this.sequenceArchiveStatusFilter).then(sequences => {
      this.sequenceCount = sequences.length;
      this.tableDataSource.data = sequences.map(sequence => ({
        ...sequence,
        inboundFeedName: this.getInboundFeedName(sequence.inboundFeedId),
        timezone: this.getSequenceDisplayTimezone(sequence),
        stepSummary: this.formatStepSummary(sequence.steps || []),
      }));
    }).catch(error => {
      console.error(error);
      this.snackBar.open('Unable to load marketing sequences. Try again in a few minutes.', '', {
        duration: 2000,
      });
    });

    this.campaignsService.getAll().then(campaigns => {
      this.campaigns = campaigns;
      this.refreshDisplayRows();
    });

    this.inboundFeedsService.listFeeds({
      scopeType: 'marketingGroup',
      status: 'all',
      limit: 1000,
    }).then(feeds => {
      this.inboundFeeds = feeds;
      this.refreshDisplayRows();
    }).catch(error => {
      console.error(error);
      this.inboundFeeds = [];
    });
  }

  add(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        this.getDefaultDialogData(),
        this.getSequenceDialogFields(false),
        'Marketing Sequence',
        true,
        'Add',
        true,
      ),
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result) {
        return;
      }

      try {
        await this.sequencesService.create(this.toSequencePayload(result));
        this.snackBar.open('Sequence created.', '', { duration: 2000 });
        this.load();
      } catch (error) {
        console.error(error);
        this.snackBar.open('Error while creating this Sequence. Try again in a few minutes.', '', {
          duration: 2000,
        });
      }
    });
  }

  edit(sequence: MarketingSequence): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        this.toSequenceDialogData(sequence),
        this.getSequenceDialogFields(true),
        'Marketing Sequence',
        true,
        'Edit',
        true,
      ),
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result) {
        return;
      }

      try {
        await this.sequencesService.updateById(sequence.id, this.toSequencePayload(result, sequence.brandingConfig));
        this.snackBar.open('Sequence updated.', '', { duration: 2000 });
        this.load();
      } catch (error) {
        console.error(error);
        this.snackBar.open('Error while updating this Sequence. Try again in a few minutes.', '', {
          duration: 2000,
        });
      }
    });
  }

  async archive(sequence: MarketingSequence): Promise<void> {
    const ok = await this.uiAlertService.presentAlertConfirm(
      'Do you really want to archive this sequence?',
    );
    if (!ok) {
      return;
    }

    try {
      await this.sequencesService.archiveById(sequence.id);
      this.snackBar.open('Sequence archived.', '', { duration: 2000 });
      this.load();
    } catch (error) {
      console.error(error);
      this.snackBar.open('Error while archiving this Sequence. Try again in a few minutes.', '', {
        duration: 2000,
      });
    }
  }

  getStatusCount(status: MarketingSequenceStatus): number {
    return this.tableDataSource.data.filter(sequence => sequence.status === status).length;
  }

  onArchiveStatusFilterChange(): void {
    this.load();
  }

  getStatusClass(status: string): string {
    return `status-${status || 'default'}`;
  }

  isArchived(sequence: MarketingSequence): boolean {
    return sequence.status === 'archived';
  }

  private getSequenceDialogFields(isEdit: boolean): Field[] {
    return [
      {
        name: 'name',
        label: 'Name',
        fieldType: FieldType.F_STRING,
        required: true,
        readOnly: isEdit,
        sectionTitle: 'Sequence Setup',
      },
      {
        name: 'description',
        label: 'Description',
        fieldType: FieldType.F_STRING,
      },
      {
        name: 'status',
        label: 'Status',
        fieldType: FieldType.F_DROPDOWN,
        values: ['Active:active', 'Paused:paused', 'Archived:archived'],
        required: true,
      },
      {
        name: 'inboundFeedId',
        label: 'Inbound Feed',
        fieldType: FieldType.F_DROPDOWN,
        values: ['None:'].concat(this.inboundFeeds.map(feed => `${feed.name}:${feed.id}`)),
        searchable: true,
        searchPlaceholder: 'Search feeds...',
        sectionTitle: 'Enrollment',
      },
      {
        name: 'timezone',
        label: 'Schedule Timezone',
        fieldType: FieldType.F_DROPDOWN,
        values: this.getTimezoneValues(),
        searchable: true,
        searchPlaceholder: 'Search timezones...',
        required: true,
      },
      {
        name: 'stepCount',
        label: 'Step Count',
        fieldType: FieldType.F_NUMBER,
        invisible: () => true,
      },
      ...this.getStepFields(),
      {
        name: 'addStepAction',
        label: 'Add Step',
        fieldType: FieldType.F_ACTION_BUTTON,
        actionIcon: 'add',
        actionDisabledResolver: form => Number(form.get('stepCount')?.value || 1) >= MAX_SEQUENCE_STEPS,
        action: form => this.addSequenceStep(form),
      },
      {
        name: 'removeStepAction',
        label: 'Remove Last Step',
        fieldType: FieldType.F_ACTION_BUTTON,
        actionIcon: 'remove',
        actionDisabledResolver: form => Number(form.get('stepCount')?.value || 1) <= 1,
        action: form => this.removeLastSequenceStep(form),
      },
    ];
  }

  private getStepFields(): Field[] {
    const fields: Field[] = [];
    for (let stepNumber = 1; stepNumber <= MAX_SEQUENCE_STEPS; stepNumber++) {
      const visibleWhenAdded = {
        invisible: (form: any) => Number(form.get('stepCount')?.value || 1) < stepNumber,
      };
      fields.push(
        {
          name: `step${stepNumber}CampaignId`,
          label: `Step ${stepNumber} Campaign`,
          fieldType: FieldType.F_DROPDOWN,
          values: this.getCampaignValues(),
          searchable: true,
          searchPlaceholder: 'Search campaigns...',
          required: true,
          sectionTitle: stepNumber === 1 ? 'Sequence Steps' : undefined,
          ...visibleWhenAdded,
        },
        {
          name: `step${stepNumber}OffsetDays`,
          label: `Step ${stepNumber} Offset Days`,
          fieldType: FieldType.F_NUMBER,
          required: true,
          ...visibleWhenAdded,
        },
        {
          ...this.getSendTimeField(stepNumber),
          ...visibleWhenAdded,
        },
      );
    }
    return fields;
  }

  private addSequenceStep(form: any): void {
    const currentCount = Number(form.get('stepCount')?.value || 1);
    const nextCount = Math.min(currentCount + 1, MAX_SEQUENCE_STEPS);
    form.get('stepCount')?.setValue(nextCount);
    form.get(`step${nextCount}OffsetDays`)?.setValue(this.getDefaultStepOffsetDays(nextCount));
    form.get(`step${nextCount}SendTime`)?.setValue('09:00');
  }

  private removeLastSequenceStep(form: any): void {
    const currentCount = Number(form.get('stepCount')?.value || 1);
    if (currentCount <= 1) {
      return;
    }

    form.get(`step${currentCount}CampaignId`)?.setValue('');
    form.get(`step${currentCount}OffsetDays`)?.setValue(this.getDefaultStepOffsetDays(currentCount));
    form.get(`step${currentCount}SendTime`)?.setValue('09:00');
    form.get('stepCount')?.setValue(currentCount - 1);
  }

  private getDefaultDialogData(): Record<string, any> {
    const data: Record<string, any> = {
      status: 'active',
      inboundFeedId: '',
      timezone: this.defaultTimezone,
      stepCount: 1,
    };
    for (let stepNumber = 1; stepNumber <= MAX_SEQUENCE_STEPS; stepNumber++) {
      data[`step${stepNumber}OffsetDays`] = this.getDefaultStepOffsetDays(stepNumber);
      data[`step${stepNumber}SendTime`] = '09:00';
    }
    return data;
  }

  private toSequenceDialogData(sequence: MarketingSequence): Record<string, any> {
    const data: Record<string, any> = {
      ...this.getDefaultDialogData(),
      name: sequence.name,
      description: sequence.description || '',
      status: sequence.status || 'active',
      inboundFeedId: sequence.inboundFeedId || '',
      timezone: this.getSequenceTimezone(sequence) || this.defaultTimezone,
    };
    const steps = [...(sequence.steps || [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .slice(0, MAX_SEQUENCE_STEPS);
    data.stepCount = Math.max(1, steps.length);
    steps.forEach((step, index) => {
      const stepNumber = index + 1;
      data[`step${stepNumber}CampaignId`] = step.campaignId;
      data[`step${stepNumber}OffsetDays`] = step.offsetDays;
      data[`step${stepNumber}SendTime`] = step.sendTime || '09:00';
    });
    return data;
  }

  private toSequencePayload(
    result: Record<string, any>,
    existingBrandingConfig: Record<string, any> | null | undefined = null,
  ): PostMarketingSequence {
    return {
      name: result.name,
      description: result.description || '',
      status: result.status || 'active',
      inboundFeedId: result.inboundFeedId || null,
      brandingConfig: {
        ...(existingBrandingConfig || {}),
        timezone: result.timezone || this.defaultTimezone,
      },
      steps: this.toSequenceSteps(result),
    };
  }

  private toSequenceSteps(result: Record<string, any>): MarketingSequenceStep[] {
    const stepCount = Math.min(Math.max(Number(result.stepCount || 1), 1), MAX_SEQUENCE_STEPS);
    return Array.from({ length: stepCount }, (_, index) => index + 1)
      .map(stepNumber => ({
        campaignId: result[`step${stepNumber}CampaignId`],
        offsetDays: Number(result[`step${stepNumber}OffsetDays`] ?? 0),
        sendTime: result[`step${stepNumber}SendTime`] || '09:00',
        sortOrder: stepNumber,
      }))
      .filter(step => !!step.campaignId);
  }

  private getDefaultStepOffsetDays(stepNumber: number): number {
    return [0, 3, 7, 10][stepNumber - 1] ?? (stepNumber - 1) * 3;
  }

  private getCampaignValues(): string[] {
    return this.campaigns.map(campaign => `${campaign.name} (${this.getChannelLabel(campaign.channel)}):${campaign.id}`);
  }

  private formatStepSummary(steps: MarketingSequenceStep[]): string {
    if (!steps.length) {
      return '-';
    }
    return [...steps]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(step => `Day ${step.offsetDays} at ${step.sendTime || '09:00'}: ${this.getCampaignName(step.campaignId)}`)
      .join(', ');
  }

  private getSendTimeField(stepNumber: number): Field {
    return {
      name: `step${stepNumber}SendTime`,
      label: `Step ${stepNumber} Send Time`,
      labelResolver: form => `Step ${stepNumber} Send Time (${form.get('timezone')?.value || this.defaultTimezone})`,
      fieldType: FieldType.F_STRING,
      placeholder: '09:00',
      pattern: '^([01]\\d|2[0-3]):[0-5]\\d$',
      hint: 'Use 24-hour HH:mm time.',
    };
  }

  private getTimezoneValues(): string[] {
    return [this.defaultTimezone]
      .concat(this.timezoneOptions)
      .filter((timezone, index, values) => timezone && values.indexOf(timezone) === index)
      .map(timezone => `${timezone}:${timezone}`);
  }

  private getSequenceTimezone(sequence: Pick<MarketingSequence, 'brandingConfig'>): string | null {
    const timezone = sequence.brandingConfig?.timezone;
    return typeof timezone === 'string' && timezone ? timezone : null;
  }

  private getSequenceDisplayTimezone(sequence: Pick<MarketingSequence, 'brandingConfig'>): string {
    return this.getSequenceTimezone(sequence) || 'Not set';
  }

  private getCampaignName(campaignId: string): string {
    return this.campaigns.find(campaign => campaign.id === campaignId)?.name || campaignId;
  }

  private getChannelLabel(channel?: string): string {
    return channel === 'sms' ? 'SMS' : 'Email';
  }

  private getInboundFeedName(inboundFeedId?: string | null): string {
    if (!inboundFeedId) {
      return '-';
    }
    return this.inboundFeeds.find(feed => feed.id === inboundFeedId)?.name || inboundFeedId;
  }

  private refreshDisplayRows(): void {
    this.tableDataSource.data = this.tableDataSource.data.map(sequence => ({
      ...sequence,
      inboundFeedName: this.getInboundFeedName(sequence.inboundFeedId),
      timezone: this.getSequenceDisplayTimezone(sequence),
      stepSummary: this.formatStepSummary(sequence.steps || []),
    }));
  }
}
